import { from, Observable } from 'rxjs';
import { noop } from 'lodash';
import { componentTreeSelectionChanged } from '@ardoq/component-selection';
import { componentPreviews$ } from '@ardoq/component-overview/componentPreviews';
import { colors } from '@ardoq/design-tokens';
import { enterPressed, moveHighlightRequested } from '@ardoq/dropdown-menu';
import { dispatchAction, reducer, streamReducer } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import { SelectComponentItem } from './components/SelectComponentItem';
import { getHoverPreviewComponentItem } from '../components/getHoverPreviewComponentItem';
import { SelectCreateComponentItem } from './components/SelectCreateComponentItem';
import { SelectWorkspaceItem } from './components/SelectWorkspaceItem';
import { SingleSelectComponentItem } from './components/SingleSelectComponentItem';
import { SingleSelectWorkspaceItem } from './components/SingleSelectWorkspaceItem';
import { getOnMouseOverHandler } from '../componentSelector/getOnMouseOverHandler';
import { enhancedScopeDataStreamReducer } from './reducers/selectComponentStreamReducer';
import { getApplyInitialTreeConfigurationRoutine } from './routines/getApplyInitialTreeConfigurationRoutine';
import { getScrollHighlightedIntoViewRoutine } from './routines/getScrollHighlightedIntoViewRoutine';
import { getSelectComponentSelectionChangedRoutine } from './routines/getSelectComponentSelectionChangedRoutine';
import { actionNamespaceSelectComponentHierarchyPane } from '@ardoq/global-consts';
import { ComponentHierarchyState } from '../componentHierarchyTypes';
import { NavigatorConfiguration } from '../types';
import { navigatorLayoutOperations } from '../reducers/navigatorLayoutOperations';
import { getSelectComponentHierarchySelection$ } from '../streams/selectComponentHierarchySelection$';
import { navigatorSelectionOperations } from '../reducers/navigatorSelectionOperations';
import { navigatorOperations } from '../reducers/navigatorOperations';
import { newItemHovered } from '../actions';
import { navigatorTreeOperations } from '../reducers/navigatorTreeOperations';
import { getQuerySelectComponent$ } from './streams/querySelectComponent$';
import { enhancedScopeDataToHierarchyStreamNoDnD } from './enhancedScopeDataToHierarchyStreamNoDnD';
import { getHandleEnterPressedMultiselectRoutine } from './routines/getHandleEnterPressedMultiselectRoutine';
import {
  disabledOptionsChanged,
  expandNodeInTree,
  permissionContextChanged,
} from '../actions/navigatorActions';
import { getScopeDataStreamWithWorkspaceOptions } from '../streams/getScopeDataStreamWithWorkspaceOptions';
import { I18nLocale } from '@ardoq/i18n';

const HoverPreviewComponentItem =
  getHoverPreviewComponentItem(SelectComponentItem);

const querySelectComponent$ = getQuerySelectComponent$(
  actionNamespaceSelectComponentHierarchyPane
);

const {
  enhancedScopeData$,
  loadingScopeDataNotification$,
  loadScopeDataError$,
  workspaceOptions$,
} = getScopeDataStreamWithWorkspaceOptions(
  querySelectComponent$,
  actionNamespaceSelectComponentHierarchyPane
);

const componentSelection$ = getSelectComponentHierarchySelection$(
  actionNamespaceSelectComponentHierarchyPane
);

export const selectComponentsWorkspaceOptions$ = workspaceOptions$;
export const selectComponentsEnhancedScopeData$ = enhancedScopeData$;

export type GetSelectComponentHierarchyPaneConfigurationArgs = {
  locale: Locale;
  onCreateNewComponent?: (name: string) => void;
  i18nLocale: I18nLocale;
  hasHoverPreview: boolean;
  isMultiselectable: boolean;
  isAsyncSearch: boolean;
  areNonMatchingResultsSelectable: boolean;
};

const getComponentItem = (
  isMultiselectable: boolean,
  hasHoverPreview: boolean
) => {
  if (!isMultiselectable) return SingleSelectComponentItem;
  return hasHoverPreview ? HoverPreviewComponentItem : SelectComponentItem;
};

export const getSelectComponentHierarchyPaneConfiguration = ({
  locale,
  onCreateNewComponent,
  i18nLocale,
  hasHoverPreview,
  isMultiselectable,
  isAsyncSearch,
  areNonMatchingResultsSelectable,
}: GetSelectComponentHierarchyPaneConfigurationArgs): NavigatorConfiguration => {
  const hoverPreviewReducers = hasHoverPreview
    ? [
        streamReducer(
          componentPreviews$,
          navigatorOperations.setComponentPreviews
        ),
        reducer(
          permissionContextChanged,
          navigatorOperations.setPermissionContext
        ),
      ]
    : [];
  const selectComponentHierarchy$: Observable<ComponentHierarchyState> =
    enhancedScopeDataToHierarchyStreamNoDnD({
      enhancedScopeData$,
      locale,
      isStrictSelection: !areNonMatchingResultsSelectable,
    });

  return {
    isMultiselectable,
    isAsyncSearch,
    eventHandlers: {
      // The component is 'preselected' by hovering it so we already know which
      // component to select on enter press.
      onComponentClick: componentId =>
        dispatchAction(
          enterPressed({ nodeId: componentId, withMetaKey: false }),
          actionNamespaceSelectComponentHierarchyPane
        ),
      onWorkspaceClick: noop,
      onScenarioClick: noop,
      onWorkspaceCloseClick: noop,
      onScenarioCloseClick: noop,
      onComponentContextMenu: null,
      onWorkspaceContextmenu: null,
      onScenarioContextmenu: noop,
      onContextMenuClicked: noop,
      onMouseOver: getOnMouseOverHandler(),
      onCreateNewComponent,
    },
    additionalRoutines: [
      getScrollHighlightedIntoViewRoutine,
      getHandleEnterPressedMultiselectRoutine,
      getSelectComponentSelectionChangedRoutine,
      getApplyInitialTreeConfigurationRoutine,
    ],
    externalReducers: [
      streamReducer(selectComponentHierarchy$, enhancedScopeDataStreamReducer),
      streamReducer(
        from([{ isViewpointMode: true }]),
        navigatorLayoutOperations.setIsViewpointMode
      ),

      streamReducer(
        componentSelection$,
        navigatorSelectionOperations.updateSelection
      ),
      streamReducer(
        loadingScopeDataNotification$,
        navigatorOperations.setDataLoadingMessage
      ),
      streamReducer(
        loadScopeDataError$,
        navigatorOperations.setDataErrorMessage
      ),
      reducer(newItemHovered, navigatorTreeOperations.newItemHovered),
      reducer(moveHighlightRequested, navigatorTreeOperations.moveHighlight),
      reducer(
        disabledOptionsChanged,
        navigatorTreeOperations.disabledOptionsChanged
      ),
      reducer(expandNodeInTree, navigatorLayoutOperations.expandNodeInTree),
      ...hoverPreviewReducers,
    ],
    componentSelection$,
    rangeSelectionMode: isMultiselectable
      ? 'extendSelection'
      : 'noRangeSelection',
    actionNamespace: actionNamespaceSelectComponentHierarchyPane,
    showWorkspaceCloseButton: true,
    shouldDimNonMatchingOptions: !areNonMatchingResultsSelectable,
    showStartCreateReferenceIcon: false,
    backgroundColor: colors.white950,
    components: {
      ComponentItem: getComponentItem(isMultiselectable, hasHoverPreview),
      WorkspaceItem: isMultiselectable
        ? SelectWorkspaceItem
        : SingleSelectWorkspaceItem,
      ScenarioItem: () => null,
      CreateComponentItem: SelectCreateComponentItem,
    },
    onViewDestroyed: () => {
      dispatchAction(
        componentTreeSelectionChanged({ treeSelection: [] }),
        actionNamespaceSelectComponentHierarchyPane
      );
    },
    isControlledContainerHeight: true,
    useClassNameForRootComponents: false,
    i18nLocale,
    locale,
  };
};
